/**
 * Converts a subset of markdown to HTML for rendering AI-generated event descriptions.
 *
 * Supported: headings, bold, italic, unordered lists, paragraphs.
 * All text is HTML-escaped before formatting to prevent XSS from model responses.
 */
export function markdownToHtml(md: string): string {
  if (!md || !md.trim()) return '';

  const lines = md.split('\n');
  const htmlParts: string[] = [];
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    const headingMatch = line.match(/^(#{1,4})\s+(.+)$/);
    if (headingMatch) {
      if (inList) {
        htmlParts.push('</ul>');
        inList = false;
      }
      const level = headingMatch[1].length;
      htmlParts.push(`<h${level}>${inlineFormat(headingMatch[2])}</h${level}>`);
      continue;
    }

    const listMatch = line.match(/^[\s]*[-*]\s+(.+)$/);
    if (listMatch) {
      if (!inList) {
        htmlParts.push('<ul>');
        inList = true;
      }
      htmlParts.push(`<li>${inlineFormat(listMatch[1])}</li>`);
      continue;
    }

    if (line.trim() === '') {
      if (inList) {
        htmlParts.push('</ul>');
        inList = false;
      }
      continue;
    }

    if (inList) {
      htmlParts.push('</ul>');
      inList = false;
    }
    htmlParts.push(`<p>${inlineFormat(line)}</p>`);
  }

  if (inList) {
    htmlParts.push('</ul>');
  }

  return htmlParts.join('');
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function inlineFormat(text: string): string {
  let result = escapeHtml(text);
  // Pull links out before the bold/italic passes so a URL containing `*` does not
  // get rewritten with <em> tags inside its href, and so `**` inside link text
  // is not consumed by the bold rule. Restored after the inline passes complete.
  // The URL portion supports a single nested () pair (Wikipedia-style URLs).
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/(?:[^()\s]|\([^()\s]*\))+)\)/g;
  const linkPlaceholders: string[] = [];
  result = result.replace(linkRegex, (_match, linkText: string, url: string) => {
    // Sentinel that no real input or AI output would produce, and that contains
    // none of the markdown trigger characters. Restored to the rendered anchor
    // after the bold/italic passes complete.
    const token = `@@RV_LINK_${linkPlaceholders.length}@@`;
    linkPlaceholders.push(
      `<a href="${url}" target="_blank" rel="noopener noreferrer">${linkText}</a>`
    );
    return token;
  });

  result = result.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  result = result.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
  result = result.replace(/(?<!\w)_(.+?)_(?!\w)/g, '<em>$1</em>');

  result = result.replace(/@@RV_LINK_(\d+)@@/g, (_match, idx) => linkPlaceholders[Number(idx)]);
  return result;
}

/** Strips markdown formatting and returns plain text (for fields that don't accept HTML). */
export function stripMarkdown(md: string): string {
  if (!md) return '';
  let text = md;
  text = text.replace(/^#{1,4}\s+/gm, '');
  // Reduce [text](url) to "text" so a short description never carries raw
  // markdown link syntax into a length-limited field.
  text = text.replace(/\[([^\]]+)\]\(https?:\/\/(?:[^()\s]|\([^()\s]*\))+\)/g, '$1');
  text = text.replace(/\*\*(.+?)\*\*/g, '$1');
  text = text.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '$1');
  text = text.replace(/(?<!\w)_(.+?)_(?!\w)/g, '$1');
  text = text.replace(/^[\s]*[-*]\s+/gm, '');
  return text.trim();
}
